import { Button, Tabs, Tooltip } from '@pankod/refine-antd'
import { Canvas } from 'fabric'
import React, { useState, useEffect } from 'react'
import {
  BookCheck,
  HardDriveDownload,
  Save,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react'
import clsx from 'clsx'

import CommonComponents from './Common'
import {
  CertificateTemplateVariables,
  UserVariable,
} from '@resources/lms-management/certificate-template/types'
import ActorComponents from './Actor'
import CustomComponents from './Custom'

type MainToolbar = {
  activeTab: 'front' | 'back'
  canvas: Canvas | null
  onAddTextbox: () => void
  onAddImage: (result: string | ArrayBuffer | null) => void
  onExportSVG: () => void
  variables?: CertificateTemplateVariables
  onCreateVariable: (data: UserVariable) => Promise<any>
  onDeleteVariable: (key: string) => Promise<any>
  onSaveClick: () => Promise<any>
  onPublishClick: () => Promise<any>
  onExportClick: () => Promise<any>
  publishLabel?: string
}

const MainToolbar = ({
  activeTab,
  canvas,
  onAddImage,
  onAddTextbox,
  onExportSVG,
  variables,
  onCreateVariable,
  onDeleteVariable,
  onPublishClick,
  onSaveClick,
  onExportClick,
  publishLabel,
}: MainToolbar) => {
  const [state, setState] = useState<'idle' | 'publishing' | 'saving'>('idle')
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(false)
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      className={clsx(
        'peer-[.canvas-grab]:*:*:pointer-events-none',
        'peer-[.canvas-grab]:!cursor-not-allowed',
        'peer-[.canvas-grab]:*:opacity-50',
        'absolute right-0 top-20 z-10 flex select-none transition-transform duration-300',
        isOpen ? '-translate-x-2' : 'translate-x-[250px]',
      )}
    >
      <div className="relative w-[250px] flex flex-col !rounded-tl-none gap-2 rounded certificate-template__main-toolbar-wrapper">
        <Button
          className="!absolute -left-8 top-0 flex items-center justify-center"
          onClick={() => setIsOpen(!isOpen)}
          type="primary"
          icon={isOpen ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
        ></Button>

        <div className="rounded certificate-template__component-picker !shadow-md">
          <Tabs size="small" centered>
            <Tabs.TabPane
              className="h-[300px] px-2 pb-2"
              key="common"
              tab="General"
              tabKey="common"
            >
              <CommonComponents
                canvas={canvas}
                onAddImage={onAddImage}
                onAddTextbox={onAddTextbox}
                onExportSVG={onExportSVG}
              />
            </Tabs.TabPane>
            <Tabs.TabPane
              className="h-[300px] px-2 pb-2"
              key="actor"
              tab="Aktor"
              tabKey="actor"
            >
              <ActorComponents
                canvas={canvas}
                variable={variables?.systemVariable || []}
              />
            </Tabs.TabPane>
            <Tabs.TabPane
              className="h-[300px] px-2 pb-2"
              key="custom"
              tab="Custom"
              tabKey="custom"
            >
              <CustomComponents
                canvas={canvas}
                variable={variables?.userVariable || []}
                onCreateVariable={onCreateVariable}
                onDeleteVariable={onDeleteVariable}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
        <div className="rounded flex flex-col gap-2 !bg-transparent">
          <Button.Group className="!shadow-md certificate-template__main-toolbar-action">
            <Button
              icon={<Save className="h-5 w-5 mr-0.5" />}
              loading={state === 'saving'}
              className="!flex gap-2 items-center justify-start"
              onClick={async () => {
                setState('saving')
                await onSaveClick()
                setState('idle')
              }}
              block
            >
              Simpan
              <div className="-ml-1 text-xs text-ellipsis overflow-hidden">
                {activeTab === 'front' ? 'Hlm. Depan' : 'Hlm. Belakang'}
              </div>
            </Button>
            <Tooltip title="Unduh template (*.svg)">
              <Button
                icon={<HardDriveDownload className="h-5 w-5" />}
                className="!flex gap-2 items-center justify-center !w-10"
                onClick={() => onExportClick()}
              />
            </Tooltip>
          </Button.Group>
          <Button
            icon={<BookCheck className="h-5 w-5" />}
            className="!flex gap-2 items-center justify-center w-full !m-0 !shadow-md"
            block
            type="primary"
            loading={state === 'publishing'}
            onClick={async () => {
              try {
                setState('publishing')
                await onPublishClick()
              } finally {
                setState('idle')
              }
            }}
          >
            {publishLabel || 'Finalisasi Template'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MainToolbar
